import debounce from 'lodash/debounce';
import {
  BAD_SEARCH_INPUT,
  SEARCH_LENGTH,
  SECTION_NAME_SEARCH_KEY,
  PRODUCT_NAME_SEARCH_KEY,
  PRODUCT_METADATA_TAGS_SEARCH_KEY,
  PRODUCT_MODIFIER_GROUP_SEARCH_KEY,
  FUSE_OPTIONS
} from 'bento-ordering/menus/constants/search';

import Fuse from 'fuse.js';

function asyncDebounce(func, wait) {
  const debounced = debounce((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);

  return (...args) =>
    new Promise((resolve, reject) => {
      debounced(resolve, reject, args);
    });
}

let fuseSearch;

export const FuseSearchService = {
  initializeFuse: menu => {
    if (!fuseSearch) {
      const { sections } = menu;
      fuseSearch = new Fuse(sections, FUSE_OPTIONS);
    }

    return fuseSearch;
  },

  getFuseSearchInstance: menu =>
    fuseSearch || (menu && FuseSearchService.initializeFuse(menu)),

  hasFuseSearchInstance: () => !!fuseSearch,

  resetFuseCollection: menu => {
    const fuse = FuseSearchService.getFuseSearchInstance(menu);
    const { sections } = menu;
    fuse.setCollection(sections);
  },

  _resolveSearchMatches: (matches, section) => {
    let result = { items: [] };

    matches.forEach(({ key, refIndex, value }) => {
      switch (key) {
        case SECTION_NAME_SEARCH_KEY:
          result = section;
          break;
        case PRODUCT_NAME_SEARCH_KEY:
          result = {
            ...section,
            items: [...result.items, section.items[refIndex]]
          };
          break;
        case PRODUCT_METADATA_TAGS_SEARCH_KEY:
          result = {
            ...section,
            items: [
              ...result.items,
              ...section.items.filter(sectionItem =>
                sectionItem.product.metadata_tags.includes(value)
              )
            ]
          };
          break;
        case PRODUCT_MODIFIER_GROUP_SEARCH_KEY:
          result = {
            ...section,
            items: [
              ...result.items,
              ...section.items.filter(sectionItem =>
                sectionItem.modifierAndOptionNames.includes(value)
              )
            ]
          };
          break;
      }
    });

    return result;
  },

  _formatSearchResults: rawResults => {
    /**
     * Format of rawResults
     *  
     * {
      item: [],
      matches: [{
        indices: [],
        key: 'item.product.name',
        refIndex: 0,
        value: 'Name'
      }],
      score: .001
    }
     */

    // First filter out results that are > .4
    const highestScoring = rawResults.filter(
      rawResult => rawResult.score <= 0.4
    );

    // Reduce list to appropriate format for rendering
    const formatted = highestScoring.reduce((acc, rawResult) => {
      const { matches, item } = rawResult;
      const resultFromMatches = FuseSearchService._resolveSearchMatches(
        matches,
        item
      );
      return [...acc, resultFromMatches];
    }, []);

    // sometimes search rawResult will show multiple matches for the same item
    // for ex: 'lett' may result in 2 matches with its modifier name 'Lettuce' and its option name 'Salad Lettuce'
    // dedupe is needed in such case
    const dedupedFormatted = formatted.map(section => ({
      ...section,
      items: Object.values(
        section.items.reduce((acc, item) => {
          acc[item.id] = item; // overwrites if dupe is found
          return acc;
        }, {})
      )
    }));

    return dedupedFormatted;
  },

  debouncedFuzzySearch: asyncDebounce(async (searchTerm, menu) => {
    if (typeof searchTerm !== 'string' || typeof menu !== 'object') {
      throw new Error(BAD_SEARCH_INPUT);
    }

    // if searchTerm is too short (under 2 chars), return
    if (searchTerm.length < SEARCH_LENGTH) {
      return { sections: [] };
    }

    const fuse = FuseSearchService.getFuseSearchInstance(menu);

    const rawResults = fuse.search(searchTerm);

    const results = FuseSearchService._formatSearchResults(rawResults);

    return { sections: results ?? [] };
  }, 500)
};
